import { Link } from 'react-router-dom';
import Seo from '../components/Seo';

const LAST_UPDATED = '26 de octubre de 2026';

/**
 * Términos y Condiciones.
 *
 * A diferencia del Aviso de Privacidad, esta página no describe un proceso
 * técnico verificable en el código (qué dato se guarda, qué proveedor lo
 * procesa): describe las condiciones bajo las que Carpepel vende. Por eso
 * el contenido se mantiene deliberadamente genérico donde no hay un hecho
 * del negocio que citar (no se inventan políticas de devolución con plazos
 * específicos, certificaciones, ni domicilio fiscal), y solo afirma lo que
 * ya es verificable en el sitio: pago con Stripe, compra sin cuenta, envío
 * cotizado con SkyDropX o coordinado por WhatsApp, papel grado alimenticio.
 *
 * Aviso para quien edite esto: no es un sustituto de revisión por un
 * abogado mercantil mexicano. La Ley Federal de Protección al Consumidor
 * exige avisos específicos para comercio electrónico (información de
 * contacto, derecho de retracto en ciertos casos, mecanismos de
 * conciliación ante PROFECO) que conviene confirmar con asesoría legal
 * antes de dar esta página por definitiva.
 */
export default function Terms() {
    return (
        <div data-testid="terms-page">
            <Seo
                title="Términos y Condiciones | Carpepel"
                description="Términos y condiciones de compra en Carpepel: precios, pago, envío, cuenta, uso del sitio y limitación de responsabilidad."
                canonicalPath="/terminos"
            />

            <div style={{ backgroundColor: 'var(--ink)' }} className="w-full py-10">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: 'var(--kraft)' }}>
                        Legal
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black text-white leading-[0.95]">
                        TÉRMINOS Y CONDICIONES
                    </h1>
                    <p className="text-sm mt-3" style={{ color: 'var(--ink-dim)' }}>
                        Última actualización: {LAST_UPDATED}
                    </p>
                </div>
            </div>

            <article className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16">
                <nav aria-label="Miga de pan" className="mb-10">
                    <ol className="flex items-center gap-2 flex-wrap font-mono text-[11px] uppercase tracking-[0.1em]">
                        <li><Link to="/" className="transition-opacity hover:opacity-70" style={{ color: 'var(--ink-soft)' }}>Inicio</Link></li>
                        <li aria-hidden="true" style={{ color: 'var(--line)' }}>/</li>
                        <li aria-current="page" style={{ color: 'var(--ink)' }}>Términos y Condiciones</li>
                    </ol>
                </nav>

                <div className="space-y-8 text-[16px] leading-relaxed text-[var(--ink)]">
                    <p>
                        Estos términos rigen el uso de carpepel.com y la compra de productos a través del
                        sitio. Al realizar un pedido, crear una cuenta o navegar el sitio, aceptas lo
                        descrito aquí. Si no estás de acuerdo con alguna condición, te pedimos no usar el
                        sitio ni completar una compra.
                    </p>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Quiénes somos</h2>
                        <p>
                            Carpepel es operado por una persona física con actividad empresarial, dedicada a
                            la distribución de papel grado alimenticio. Para cualquier duda sobre un pedido o
                            sobre estos términos, puedes escribirnos a{' '}
                            <a href="mailto:admin@carpepel.com" className="underline" style={{ color: 'var(--kraft-deep)' }}>
                                admin@carpepel.com
                            </a>.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Productos y descripciones</h2>
                        <p>
                            Procuramos que las fotografías, gramajes, medidas y descripciones de cada
                            producto sean precisos. Aun así, pueden existir variaciones menores entre lotes
                            de producción (tono de color, corte) propias del papel como material, y las
                            imágenes pueden diferir ligeramente del producto físico por condiciones de
                            pantalla o iluminación. Si recibes un producto que no corresponde con lo
                            descrito o pedido, contáctanos para resolverlo.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Precios</h2>
                        <p className="mb-3">
                            Los precios se muestran en pesos mexicanos (MXN) e incluyen los impuestos
                            aplicables, salvo que se indique lo contrario. El costo de envío no está
                            incluido en el precio del producto: se cobra según la opción de envío que
                            elijas (ver la sección «Envío»).
                        </p>
                        <p>
                            <strong>Los precios pueden cambiar en cualquier momento y sin previo aviso</strong>,
                            incluyendo entre el momento en que agregas un producto al carrito y el momento en
                            que completas el pago. El precio que aplica a un pedido ya pagado es el que se
                            mostró y confirmó al momento de completar esa compra, no precios anteriores o
                            posteriores. Un error evidente de precio (por ejemplo, causado por una falla
                            técnica) no obliga a Carpepel a vender a ese precio; en ese caso te avisaremos y,
                            si ya pagaste, se te reembolsará el pedido correspondiente.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Cuenta de usuario</h2>
                        <p>
                            No necesitas una cuenta para comprar. Si decides crear una, te sirve para
                            consultar tu historial de pedidos; eres responsable de mantener la
                            confidencialidad de tu contraseña y de toda actividad que ocurra desde tu
                            cuenta. Avísanos de inmediato si sospechas un uso no autorizado.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Pago</h2>
                        <p>
                            El pago se procesa a través de Stripe, una pasarela de pago certificada. Carpepel
                            no almacena los datos de tu tarjeta. Un pedido se considera confirmado hasta que
                            el pago es aprobado por Stripe; si el pago es rechazado o no se completa, el
                            pedido no se procesa. El comprobante se envía al correo que escribas en la
                            página de pago de Stripe.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Envío</h2>
                        <p className="mb-3">Puedes elegir entre dos opciones al pagar:</p>
                        <ul className="space-y-2 pl-0 mb-3">
                            <li className="flex gap-3">
                                <span className="mt-[10px] h-px w-4 flex-shrink-0" style={{ backgroundColor: 'var(--kraft-deep)' }} aria-hidden="true" />
                                <span>
                                    <strong>Pagar solo el papel y coordinar el envío por WhatsApp.</strong> El pago
                                    en línea cubre únicamente los productos. Después del pago, el costo del envío
                                    se cotiza por WhatsApp (+52 55 8403 2954) según tu dirección y se cobra por
                                    separado antes de despachar el pedido. Si no estás de acuerdo con el costo de
                                    envío cotizado, puedes cancelar el pedido antes de que se despache y se te
                                    reembolsará lo que pagaste.
                                </span>
                            </li>
                            <li className="flex gap-3">
                                <span className="mt-[10px] h-px w-4 flex-shrink-0" style={{ backgroundColor: 'var(--kraft-deep)' }} aria-hidden="true" />
                                <span>
                                    <strong>Cotizar el envío en el sitio y pagar todo junto.</strong> El costo y el
                                    tiempo estimado de entrega se cotizan según tu código postal a través de
                                    SkyDropX antes de confirmar el pago.
                                </span>
                            </li>
                        </ul>
                        <p>
                            Los tiempos de entrega mostrados
                            son estimados de la paquetería y no son garantizados por Carpepel: pueden variar
                            por causas ajenas a nosotros, incluyendo la operación de la paquetería, clima, o
                            zonas de difícil acceso. Es tu responsabilidad proporcionar una dirección de
                            entrega correcta y completa; los envíos con dirección incorrecta o incompleta
                            pueden retrasarse o generar un costo adicional de reenvío.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Cancelaciones, cambios y devoluciones</h2>
                        <p>
                            Si necesitas cancelar o modificar un pedido, contáctanos lo antes posible a{' '}
                            <a href="mailto:admin@carpepel.com" className="underline" style={{ color: 'var(--kraft-deep)' }}>
                                admin@carpepel.com
                            </a>{' '}
                            o por WhatsApp: si el pedido aún no ha sido enviado, haremos lo posible por
                            atenderlo. Una vez que el pedido ha sido despachado, la cancelación ya no es
                            posible. Si el producto llega dañado, incompleto o no corresponde con tu pedido,
                            contáctanos dentro de los días siguientes a la entrega con fotografías del
                            problema para evaluar una reposición o reembolso. No podemos aceptar devoluciones
                            de producto que ya fue usado o manipulado, salvo que el defecto sea atribuible a
                            Carpepel.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Uso del sitio</h2>
                        <p className="mb-3">Al usar el sitio te comprometes a no:</p>
                        <ul className="space-y-2 pl-0">
                            {[
                                'Usarlo para fines ilegales o no autorizados.',
                                'Intentar acceder sin autorización a cuentas de otras personas, a nuestros sistemas o a nuestra base de datos.',
                                'Interferir con el funcionamiento del sitio (por ejemplo, mediante ataques automatizados o extracción masiva de datos).',
                                'Usar el contenido del sitio (textos, fotografías, logotipo) fuera de lo permitido por estos términos, sin autorización por escrito.',
                            ].map((item, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="mt-[10px] h-px w-4 flex-shrink-0" style={{ backgroundColor: 'var(--kraft-deep)' }} aria-hidden="true" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-3">
                            Nos reservamos el derecho de suspender o cancelar cuentas y pedidos que
                            incumplan estos términos, o que muestren indicios de fraude.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Propiedad intelectual</h2>
                        <p>
                            El contenido del sitio —textos, fotografías de producto, logotipo y diseño— es
                            propiedad de Carpepel o se usa con la autorización correspondiente. No está
                            permitido reproducirlo con fines comerciales sin nuestro consentimiento por
                            escrito.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Limitación de responsabilidad</h2>
                        <p>
                            Carpepel distribuye papel grado alimenticio para uso comercial; no fabrica el
                            producto ni realiza pruebas de laboratorio propias sobre cada lote. En la medida
                            permitida por la ley aplicable, Carpepel no será responsable por daños indirectos,
                            pérdida de ganancias, o daños derivados del uso del producto en un fin distinto al
                            previsto (envoltura y contacto con alimentos), del mal manejo o almacenamiento
                            del producto después de la entrega, ni de retrasos de la paquetería. Esto no
                            limita ningún derecho que la ley te reconozca de forma irrenunciable como
                            consumidor.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Ley aplicable</h2>
                        <p>
                            Estos términos se rigen por las leyes de los Estados Unidos Mexicanos. Para
                            cualquier controversia que no pueda resolverse directamente, puedes acudir a la
                            Procuraduría Federal del Consumidor (PROFECO), sin perjuicio de acudir a los
                            tribunales competentes.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Cambios a estos términos</h2>
                        <p>
                            Podemos actualizar estos términos en cualquier momento, por ejemplo para
                            reflejar cambios en nuestros procesos de pago, envío o políticas de precio. Los
                            cambios aplican a partir de su publicación en esta página; la fecha de la versión
                            vigente siempre aparece al inicio. El uso continuado del sitio después de un
                            cambio implica su aceptación.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Contacto</h2>
                        <p>
                            Para dudas sobre estos términos, un pedido o una compra, escríbenos a{' '}
                            <a href="mailto:admin@carpepel.com" className="underline" style={{ color: 'var(--kraft-deep)' }}>
                                admin@carpepel.com
                            </a>.
                        </p>
                    </section>
                </div>
            </article>
        </div>
    );
}
